import { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash-es/isEmpty";

import Select from "react-select";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionTitle from "../../components/AccordionTitle";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import TableRowNoResults from "../../components/TableRowNoResults";
import { StripedTableCell, StripedTableRow } from "../../components/StripedTable";
import { getPeriodName } from "../../services/SchoolService";

import { doService } from "../../services/ServiceHelper";

import { getSchoolStudents } from "../../services/UserService";
import { getOpenStudentPases } from "../../services/PassService";
import { getYYYYMMDD } from "../../services/DateService";
import { getStudentActivitiesByFaculty } from "../../services/ActivityService";

const FindStudent = () => {
  const navigate = useNavigate();

  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [_students, setStudents] = useState([]);
  const [_openPasses, setOpenPasses] = useState([]);
  const [_selectedStudent, setSelectedStudent] = useState({});
  const [_activities, setActivities] = useState([]);
  const [showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    initStudents();
  }, []);

  const initStudents = () => {
    setShowProgress(true);

    doService(
      getSchoolStudents(_school, _jwt),
      (results) => {
        const labeledStudents = [];
        for (const student of results.data) {
          let label = `${student.last_name}, ${student.first_name}`;

          if (!isEmpty(student.mi)) {
            label += ` ${student.mi}.`;
          }

          label += ` (${student.grade})`;

          labeledStudents.push({
            label: label,
            value: student.eid,
          });
        }
        setStudents(labeledStudents);
      },
      null,
      null,
      () => setShowProgress(false)
    );
  };

  const handleSelect = (obj) => {
    setSelectedStudent(obj);

    if (obj == null) return;

    // load data for the selected student
    const eid = obj.value;

    setShowProgress(true);
    initOpenPasses(eid);
    initActivities(eid);
  };

  const initActivities = (eid) => {
    const date = getYYYYMMDD();
    doService(
      getStudentActivitiesByFaculty(_school, _jwt, eid, date),
      (results) => {
        setActivities(results.data);
        setSearchPerformed(true);
        setShowProgress(false);
      }
    );
  };

  const initOpenPasses = (eid) => {
    const date = getYYYYMMDD();
    doService(getOpenStudentPases(_school, _jwt, eid, date), (results) => {
      setOpenPasses(results.data);
      setSearchPerformed(true);
      setShowProgress(false);
    });
  };

  const handleClickViewStudentCalendar = () => {
    navigate(`/faculty/student-calendar/${_selectedStudent.value}`);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Find Student" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text="Select Student" />
          </AccordionSummary>
          <AccordionDetails>
            <InputLabel>Type Student Name *</InputLabel>
            <Select options={_students} onChange={handleSelect} />
          </AccordionDetails>
        </Accordion>

        {_searchPerformed && (
          <>
            <Button
              variant="contained"
              onClick={handleClickViewStudentCalendar}
              sx={{ mt: 2 }}
            >
              View Student Calendar
            </Button>

            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle text="Activity Details" />
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 0, mb: 1 }}>
                  <Table aria-label="open passes" size="small">
                    <TableBody>
                      {_activities.map((activity) => (
                        <StripedTableRow>
                          <StripedTableCell>
                            {activity.location_name === "" && (
                              <span style={{ fontWeight: 800 }}>
                                {getPeriodName(_school, activity.period-1)}: NO ACTIVITY SELECTED
                              </span>
                            )}
                            {activity.location_name !== "" && (
                              <>
                                <span style={{ fontWeight: 800 }}>
                                  {activity.period}: {activity.name}
                                  {activity.attendance_taken === 1 && (
                                    <> (ATTENDANCE TAKEN)</>
                                  )}
                                  {activity.attendance_taken === 0 && (
                                    <> (ATTENDANCE NOT TAKEN)</>
                                  )}
                                </span>
                                {activity.is_late === 1 && <> [LATE]</>}
                                {activity.is_late === 0 && <> [PRESENT]</>}
                                {activity.is_late == null &&
                                  activity.attendance_taken === 1 && (
                                    <> [ABSENT]</>
                                  )}
                                <br />
                                {activity.owner} @ {activity.location_name}
                              </>
                            )}
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle text="Open Passes" />
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 0, mb: 1 }}>
                  <Table aria-label="open passes" size="small">
                    <TableBody>
                      {_openPasses.map((pass) => (
                        <StripedTableRow key={pass.eid}>
                          <StripedTableCell>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell variant="head">Status:</TableCell>
                                  <TableCell>{pass.statusText}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell variant="head">To:</TableCell>
                                  <TableCell>
                                    {pass.to_location_name != null && (
                                      <>(L) {pass.to_location_name}</>
                                    )}
                                    {pass.to_location_name == null && (
                                      <>
                                        (T) {pass.to_teacher_last_name},{" "}
                                        {pass.to_teacher_first_name}
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell variant="head">From:</TableCell>
                                  <TableCell>
                                    {pass.teacher_last_name},{" "}
                                    {pass.teacher_first_name}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell variant="head">Type:</TableCell>
                                  <TableCell>{pass.type_name}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell variant="head">Created:</TableCell>
                                  <TableCell>{pass.created_at}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                      <TableRowNoResults data={_openPasses} />
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>
    </>
  );
};

export default FindStudent;
